import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { isEnterKey } from '~/libs/keyboard'

type Props = {
  isOpen: boolean
  /** 「閉じる」= 変更を破棄してモーダルを閉じる。 */
  onConfirm: () => void
  /** 「キャンセル」= モーダルに戻る。 Esc / オーバーレイクリック / × も同じ。 */
  onCancel: () => void
}

/**
 * 未保存の入力があるモーダルを閉じようとした時の確認ダイアログ。
 *
 * 8080 プロトタイプ (`js/unsaved-guard.js` の `showDialog`) の仕様を踏襲する:
 * - タイトル「未保存の変更」 (design/CLAUDE.md:325「modal のタイトルは体言止め」。
 *   2026-08-28 デザイナー指摘で「未保存の変更があります」 から改めた。 8080 の
 *   `.ecl-modal-title` も「未保存の変更」) / 本文「行った変更が保存されない可能性があります。 …」
 * - ボタンは キャンセル (既定 = TertiaryButton) と 破棄側 (negative 色 = DangerButton)
 * - Esc = キャンセル / オーバーレイクリック = キャンセル
 * - Enter = **focus 中のボタンの活性化**。dialog を開くと DS が focus trap で
 *   ボタンへ focus を移すため、実際には「キャンセル」が押される (= 安全側)。
 *   下の keydown handler が `onConfirm` を呼ぶのは focus が対話要素の外にある
 *   場合だけで、通常の操作では発火しない (レビュー指摘 2026-08-12 で実測確認)。
 * - beforeunload (Chrome 標準ダイアログ) は使わない — 8080 が「UI 統一感を
 *   欠くため廃止」と明記しているため、 本ダイアログでも復活させない。
 *
 * **[8080 差異 1] 文言の動詞**: 8080 はページ遷移ガードのため「移動しますか?」/
 * 「移動する」だが、 本ダイアログはモーダルの離脱なので「閉じますか?」/「閉じる」に
 * 置き換えている (移動しないので「移動する」は誤導)。 タイトルと本文前半は 8080 のまま。
 *
 * **[8080 差異 2] Enter の扱い**: 8080 は Enter で無条件に続行する。 本実装は
 * 「破棄」 という不可逆操作を Enter 一発で確定させないため、 安全側 (キャンセル) に
 * 倒れる。 8080 は focus 管理を持たない素の JS
 * だったため無条件に Enter で続行していた。 DS の dialog は focus trap を持ち、
 * ボタンに focus がある状態で Enter を奪うとキーボード操作 (focus したボタンの
 * 活性化) が壊れるため、 **focus がボタン等の対話要素上に無い時だけ** Enter を
 * 続行として扱う。
 */
export const UnsavedChangesDialog = ({
  isOpen,
  onConfirm,
  onCancel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (!isEnterKey(e) || e.repeat) {
        return
      }
      // [8080 差異 2] 対話要素に focus がある時はネイティブの活性化を優先する。
      const active = document.activeElement
      if (
        active instanceof HTMLElement &&
        active.closest('button, a[href], input, select, textarea') !== null
      ) {
        return
      }
      e.preventDefault()
      onConfirm()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onConfirm])

  return (
    <ConfirmDialog
      body={t('common:unsavedGuard.body')}
      cancelLabel={t('common:action.cancel')}
      confirmLabel={t('common:unsavedGuard.confirm')}
      // 未保存の入力を捨てる操作なので negative 色 (DangerButton)。
      // 8080 の「移動する」も `--surface-negative-high` を当てている。
      destructive
      isOpen={isOpen}
      onCancel={onCancel}
      onConfirm={onConfirm}
      title={t('common:unsavedGuard.title')}
    />
  )
}
